import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { surveyIdAtom } from '@/_atoms/survey';
import { respondentAtom } from '@/_atoms/respondent';
import { batchGetQuery } from '@/_queries/batchQueries';

import type { AxiosResponse } from 'axios';
import type { Batch, BatchView, Material } from '@/types/batch';

/** Return the batch id (an uuid). */
export const batchIdAtom = atom<string | null>((get) =>
  get(respondentAtom)?.batch ?? null
);

/** Fetch the Batch object from the API based on the batch property of the respondent atom. */
export const batchViewAtom = atomWithQuery<BatchView | null>((get) => {
  const batchId: string | null = get(batchIdAtom);

  return {
    queryKey: ['batch', batchId],
    queryFn: async () => {
      if (!batchId) return null;
      const response: AxiosResponse<Batch> = await batchGetQuery(batchId);
      const batch: Batch = response.data;

      // noinspection SpellCheckingInspection
      return {
        batchId: batch.id,
        autoResumeOnLogin: batch.autoResumeOnLogin,
        debrief: batch.debrief,
        carelessResponseChecks: batch.carelessResponseChecks,
        emailAddressVerificationTemplate: batch.emailAddressVerificationTemplate,
        hasFinishButton: batch.hasFinishButton,
        hasOptionalMaterials: batch.materials.some((m: Material) => !m.isRequired),
        hasReenterableMaterials: batch.materials.some((m: Material) => m.canReenterAfterCompletion),
        hasWelcome: !!batch.welcome?.body,
        languages: batch.languages,
        lastSurveyId: batch.materials?.at(-1)?.survey ?? null,
        loginCodeEmailTemplate: batch.loginCodeEmailTemplate,
        materials: batch.materials,
        respondentLoginCodeDuration: batch.respondentLoginCodeDuration,
        slugMap: new Map(batch.materials.map(m => [m.slug, m.survey])),
        welcome: batch.welcome
      } as BatchView;
    },
    enabled: !!batchId,
    staleTime: Infinity, // never refetch unless manually
    gcTime: Infinity,    // never garbage collect
  };
});

/**
 * Derived atom that returns a list of all material in the batch.
 *
 * @returns A list of all `Material` in the batch.
 */
export const materialsAtom = atom<Material[]>((get) => {
  return get(batchViewAtom).data?.materials ?? [];
});

/**
 * Derived atom that returns the current material for the active survey.
 *
 * @returns The matching `Material`, or `null` if no survey is active or no match is found.
 */
export const currentMaterialAtom = atom<Material | null>((get) => {
  const surveyId: string | null = get(surveyIdAtom);
  if (!surveyId) return null;

  const materials: Material[] = get(materialsAtom);
  return materials.find((m) => m.survey === surveyId) ?? null;
});